/**
 * Image Hotspot Player (Listen & Color)
 * Layout: tabs / stacked / split - renders Color Drop Zone and optional supplementary content
 */

import * as React from 'react';
import { cn, Tabs, TabsList, TabsTrigger, TabsContent } from '@ai-content-hub/content-kit';
import type { GradingResult } from '@ai-content-hub/content-kit';
import {
    ColorDropZonePlayer,
    PagePlayer,
    type ColorDropZoneAnswer,
} from '@ai-content-hub/content-elements';
import { isCorrectAnswerRevealed, useLocale } from '@ai-content-hub/content-core';
import type { ImageHotspotData, ImageHotspotConfig, PlayerViewLayout } from './schema';
import { defaultImageHotspotData } from './schema';
import { MapPin, BookOpen } from 'lucide-react';

type TabValue = 'image' | 'content';

export interface ImageHotspotPlayerProps {
    data?: ImageHotspotData;
    value?: ImageHotspotData;
    config?: ImageHotspotConfig;
    answer?: ColorDropZoneAnswer;
    onAnswer?: (answer: ColorDropZoneAnswer) => void;
    disabled?: boolean;
    showFeedback?: boolean;
    result?: GradingResult;
    isReview?: boolean;
    className?: string;
}


export const ImageHotspotPlayer: React.FC<ImageHotspotPlayerProps> = ({
    data: dataProp,
    value: valueProp,
    config,
    answer: answerProp,
    onAnswer: onAnswerProp,
    disabled = false,
    showFeedback = false,
    result,
    isReview = false,
    className,
}) => {
    const data = dataProp ?? valueProp;
    const [activeTab, setActiveTab] = React.useState<TabValue>('image');
    const { t } = useLocale({ config: config?.i18n });
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false.
    const revealCorrect = isCorrectAnswerRevealed(config);

    const tabs: { value: TabValue; label: string; icon: typeof MapPin }[] = React.useMemo(() => [
        { value: 'image', label: t('imageTab', 'Image'), icon: MapPin },
        { value: 'content', label: t('contentTab', 'Content'), icon: BookOpen },
    ], [t]);

    const safeData: ImageHotspotData = {
        ...defaultImageHotspotData,
        ...data,
        colorDropZoneData: data?.colorDropZoneData ?? defaultImageHotspotData.colorDropZoneData,
        supplementaryContent: data?.supplementaryContent ?? defaultImageHotspotData.supplementaryContent,
    };

    const playerView: PlayerViewLayout = config?.layout?.playerView ?? 'stacked';
    const hasRootDescription = Boolean(safeData.description?.trim());

    const instructionsHeader = hasRootDescription ? (
        <div className="image-hotspot-player-header image-hotspot-player-header--intro flex-shrink-0 space-y-2">
            <p className="image-hotspot-player-description text-sm text-muted-foreground leading-relaxed">
                {safeData.description}
            </p>
        </div>
    ) : null;

    const [localAnswer, setLocalAnswer] = React.useState<ColorDropZoneAnswer>({});
    const answer = answerProp ?? localAnswer;
    const handleAnswer = React.useCallback(
        (next: ColorDropZoneAnswer) => {
            if (onAnswerProp) {
                onAnswerProp(next);
            } else {
                setLocalAnswer(next);
            }
        },
        [onAnswerProp]
    );

    const interactivePanel = (
        <ColorDropZonePlayer
            data={safeData.colorDropZoneData}
            answer={answer}
            onAnswer={handleAnswer}
            disabled={disabled}
            showFeedback={showFeedback}
            result={result}
            isReview={isReview}
            config={config as Record<string, unknown> | undefined}
        />
    );

    const submitted = showFeedback && (!!result || isReview);
    const contentPanel = (
        <PagePlayer data={safeData.supplementaryContent!} submitted={submitted && revealCorrect} />
    );

    const hasSupplementary =
        safeData.supplementaryContent?.sections?.length &&
        safeData.supplementaryContent.sections.length > 0;

    if (!hasSupplementary) {
        return (
            <div className={cn('image-hotspot-player flex flex-col gap-6', className)}>
                {instructionsHeader}
                {interactivePanel}
            </div>
        );
    }

    if (playerView === 'stacked') {
        return (
            <div
                className={cn(
                    'image-hotspot-player image-hotspot-player--stacked flex flex-col gap-6',
                    className
                )}
            >
                {instructionsHeader}
                <div className="image-hotspot-player-content">
                    <div className="image-hotspot-player-content-header flex items-center gap-2 pb-3 border-b mb-3">
                        <BookOpen className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">{t('contentTab', 'Content')}</h3>
                    </div>
                    {contentPanel}
                </div>
                <div className="image-hotspot-player-image">
                    <div className="image-hotspot-player-image-header flex items-center gap-2 pb-3 border-b mb-3">
                        <MapPin className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">{t('imageTab', 'Image')}</h3>
                    </div>
                    {interactivePanel}
                </div>

            </div>
        );
    }

    if (playerView === 'split') {
        return (
            <div
                className={cn(
                    'image-hotspot-player image-hotspot-player--split flex flex-col gap-6',
                    className
                )}
            >
                {instructionsHeader}
                <div className="image-hotspot-player-split grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="image-hotspot-player-image flex flex-col min-h-0">
                    <div className="image-hotspot-player-image-header flex items-center gap-2 pb-3 border-b mb-3">
                        <MapPin className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">{t('imageTab', 'Image')}</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto">{interactivePanel}</div>
                </div>
                <div className="image-hotspot-player-content flex flex-col min-h-0 lg:border-l lg:pl-6">
                    <div className="image-hotspot-player-content-header flex items-center gap-2 pb-3 border-b mb-3">
                        <BookOpen className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">{t('contentTab', 'Content')}</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto">{contentPanel}</div>
                </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cn('image-hotspot-player image-hotspot-player--tabs flex flex-col gap-6', className)}>
            {instructionsHeader}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
                <TabsList variant="primary" className="w-full h-auto px-1 py-1 mb-4">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <TabsTrigger
                                key={tab.value}
                                value={tab.value}
                                className="image-hotspot-player-tab flex-1 py-3 px-4 text-sm font-semibold"
                            >
                                <Icon className="h-4 w-4" />
                                <span>{tab.label}</span>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>
                <TabsContent value="content" className="image-hotspot-player-content flex-1 min-h-0 overflow-auto mt-0">
                    {contentPanel}
                </TabsContent>
                <TabsContent value="image" className="image-hotspot-player-image flex-1 min-h-0 overflow-auto mt-0">
                    {interactivePanel}
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default ImageHotspotPlayer;
